/**
 * useFlowData - Transform backend FlowSnapshot to visualization format
 *
 * This hook takes a FlowSnapshot from the backend API and transforms it
 * into the node/edge types required by the FlowVisualization component.
 *
 * Phase 2 of WP-FLOW-5: Data Integration
 */

import { useMemo } from 'react';
import type { FlowSnapshot } from '@/services/flows';
import type {
  InflowNode,
  AccountNode,
  OutflowNode,
  LumpedNode,
  FlowEdge as VisualizationFlowEdge,
  TransferData,
  TransferSankeyData,
} from '../types';
import { COLORS } from '../constants';

/**
 * Result of the useFlowData transformation
 */
export interface FlowDataResult {
  inflowNodes: InflowNode[];
  accountNodes: AccountNode[];
  /** Pre-lumped nodes from backend (when tiers are not expanded) */
  lumpedNodes: LumpedNode[];
  outflowNodes: OutflowNode[];
  edges: VisualizationFlowEdge[];
  /** @deprecated Use transferSankey instead (WP-FLOW-REFACTOR-2) */
  transfers: TransferData[];
  /** WP-FLOW-REFACTOR-2: Transfer Sankey data for visualization */
  transferSankey?: TransferSankeyData;
  summary: {
    totalIncome: number;
    totalExpenses: number;
    netFlow: number;
    savingsRate: number;
  };
}

/**
 * Determine account tier based on node label from backend
 *
 * Tier classification:
 * - A (Operating): depository (checking/savings for daily use), credit cards
 * - B (Savings & Investments): investment, brokerage, 401k, IRA, etc.
 * - C (Other Assets): real_estate, vehicle, crypto, etc.
 * - D (Other Liabilities): mortgage, auto loan, student loan, etc.
 *
 * Note: Backend sends type as 'account' for all accounts,
 * so we determine tier from the label using heuristics.
 */
function determineTier(nodeLabel: string | undefined): 'A' | 'B' | 'C' | 'D' {
  // Defensive: handle undefined/empty labels (default to Tier A)
  if (!nodeLabel) {
    return 'A';
  }
  const lowerLabel = nodeLabel.toLowerCase();

  // Tier A: Operating accounts (daily use)
  if (
    lowerLabel.includes('checking') ||
    lowerLabel.includes('credit') ||
    lowerLabel.includes('cc') ||
    lowerLabel.includes('card')
  ) {
    return 'A';
  }

  // Tier B: Savings & Investments
  if (
    lowerLabel.includes('savings') ||
    lowerLabel.includes('401k') ||
    lowerLabel.includes('401(k)') ||
    lowerLabel.includes('ira') ||
    lowerLabel.includes('roth') ||
    lowerLabel.includes('brokerage') ||
    lowerLabel.includes('investment') ||
    lowerLabel.includes('hsa') ||
    lowerLabel.includes('529')
  ) {
    return 'B';
  }

  // Tier D: Liabilities (loans, mortgages, debt)
  // Check before Tier C since some labels might match both
  if (
    lowerLabel.includes('mortgage') ||
    lowerLabel.includes('loan') ||
    lowerLabel.includes('student') ||
    lowerLabel.includes('auto loan') ||
    lowerLabel.includes('home equity') ||
    lowerLabel.includes('heloc') ||
    lowerLabel.includes('debt')
  ) {
    return 'D';
  }

  // Tier C: Other assets
  if (
    lowerLabel.includes('property') ||
    lowerLabel.includes('real estate') ||
    lowerLabel.includes('rental') ||
    lowerLabel.includes('annuity') ||
    lowerLabel.includes('vehicle') ||
    lowerLabel.includes('crypto')
  ) {
    return 'C';
  }

  // Default to Tier A for unclassified accounts
  return 'A';
}

/**
 * Determine account type from node label/type
 */
function determineAccountType(
  nodeLabel: string | undefined
): 'depository' | 'credit' | 'investment' | 'loan' | 'other' {
  // Defensive: handle undefined/empty labels (default to 'other')
  if (!nodeLabel) {
    return 'other';
  }
  const lowerLabel = nodeLabel.toLowerCase();

  if (
    lowerLabel.includes('credit') ||
    lowerLabel.includes('cc') ||
    lowerLabel.includes('card')
  ) {
    return 'credit';
  }

  if (
    lowerLabel.includes('checking') ||
    lowerLabel.includes('savings') ||
    lowerLabel.includes('money market')
  ) {
    return 'depository';
  }

  if (
    lowerLabel.includes('401k') ||
    lowerLabel.includes('401(k)') ||
    lowerLabel.includes('ira') ||
    lowerLabel.includes('roth') ||
    lowerLabel.includes('brokerage') ||
    lowerLabel.includes('investment') ||
    lowerLabel.includes('hsa') ||
    lowerLabel.includes('529')
  ) {
    return 'investment';
  }

  if (
    lowerLabel.includes('mortgage') ||
    lowerLabel.includes('loan') ||
    lowerLabel.includes('auto loan')
  ) {
    return 'loan';
  }

  return 'other';
}

/**
 * Map backend node ID to visualization node ID format
 *
 * The backend uses IDs like "income", "account-{uuid}", "expense-{uuid}", "tier-B-lumped"
 * The visualization expects consistent prefixed IDs
 */
function mapNodeId(backendId: string, nodeType: string): string {
  // If the ID already has a prefix, use it as-is
  if (
    backendId.startsWith('income-') ||
    backendId.startsWith('account-') ||
    backendId.startsWith('expense-') ||
    backendId.startsWith('tier-')  // Lumped node IDs (e.g., "tier-B-lumped")
  ) {
    return backendId;
  }

  // Add prefix based on node type
  switch (nodeType) {
    case 'income':
      return `income-${backendId}`;
    case 'account':
      return `account-${backendId}`;
    case 'expense':
      return `expense-${backendId}`;
    default:
      return backendId;
  }
}

/**
 * Check if a node type represents an account (including lumped accounts)
 */
function isAccountType(type: string | undefined): boolean {
  return type === 'account' || type === 'lumped_account';
}

/**
 * Determine edge type based on source and target nodes
 */
function determineEdgeType(
  sourceId: string,
  targetId: string,
  nodes: Map<string, { type: string }>
): 'income' | 'expense' | 'transfer' {
  const sourceNode = nodes.get(sourceId);
  const targetNode = nodes.get(targetId);

  // Income: from income node to account
  if (sourceNode?.type === 'income') {
    return 'income';
  }

  // Expense: from account to expense node
  if (targetNode?.type === 'expense') {
    return 'expense';
  }

  // Transfer: between accounts (including lumped accounts)
  if (isAccountType(sourceNode?.type) && isAccountType(targetNode?.type)) {
    return 'transfer';
  }

  // Default based on ID patterns
  if (sourceId.startsWith('income-') || sourceId === 'income') {
    return 'income';
  }
  if (targetId.startsWith('expense-')) {
    return 'expense';
  }

  return 'transfer';
}

/**
 * Transform backend FlowSnapshot to visualization format
 *
 * @param snapshot - FlowSnapshot from backend API
 * @returns Transformed data ready for FlowVisualization component, or null if no data
 */
export function useFlowData(
  snapshot: FlowSnapshot | undefined
): FlowDataResult | null {
  return useMemo(() => {
    if (!snapshot) {
      return null;
    }

    const { nodes, edges, summary } = snapshot;

    // If no nodes, return null (empty state)
    if (!nodes || nodes.length === 0) {
      return null;
    }

    // Build a map of nodes for quick lookup
    const nodeMap = new Map<string, { type: string; label: string }>();
    for (const node of nodes) {
      nodeMap.set(node.id, { type: node.type, label: node.label });
    }

    // Extract and transform income nodes
    const inflowNodes: InflowNode[] = nodes
      .filter((n) => n.type === 'income')
      .map((n) => ({
        id: mapNodeId(n.id, 'income'),
        type: 'income' as const,
        name: n.label,
        value: Math.abs(n.total),
        color: COLORS.income,
        categoryId: n.entityId ?? n.id,  // Use entityId for navigation, fallback to id
      }));

    // Extract and transform regular account nodes (NOT lumped_account)
    const accountNodes: AccountNode[] = nodes
      .filter((n) => n.type === 'account')
      .map((n) => ({
        id: mapNodeId(n.id, 'account'),
        type: 'account' as const,
        name: n.label,
        // Balance is derived from total for now (backend may add balance field later)
        balance: n.total,
        // flowTotal is the absolute value of transaction activity
        flowTotal: Math.abs(n.total),
        // Use backend's tier if provided, otherwise determine from label
        tier: (n.tier as 'A' | 'B' | 'C' | 'D') ?? determineTier(n.label),
        accountType: determineAccountType(n.label),
        accountId: n.entityId ?? n.id,  // Use entityId for navigation, fallback to id
      }));

    // Build lookup map from account ID (without prefix) to AccountNode
    // This allows matching contained_accounts UUIDs to actual AccountNode objects
    const accountNodeById = new Map<string, AccountNode>();
    for (const acc of accountNodes) {
      // Map by accountId (the raw UUID, used in contained_accounts)
      accountNodeById.set(acc.accountId, acc);
      // Also map by full ID (account-{uuid}) for edge routing
      accountNodeById.set(acc.id, acc);
    }

    // Extract and transform lumped_account nodes from backend
    // These represent pre-aggregated Tier B/C when tiers are not expanded
    const lumpedNodes: LumpedNode[] = nodes
      .filter((n) => n.type === 'lumped_account')
      .map((n) => {
        const tier = (n.tier as 'B' | 'C' | 'D') ?? 'B';
        // Read account count and flow values from backend metadata
        const metadata = n.metadata ?? {};
        const accountCount = typeof metadata.account_count === 'number' ? metadata.account_count : 0;
        const aggregateInflow = typeof metadata.aggregate_inflow === 'string'
          ? Math.abs(parseFloat(metadata.aggregate_inflow))
          : Math.abs(n.total);
        const aggregateOutflow = typeof metadata.aggregate_outflow === 'string'
          ? Math.abs(parseFloat(metadata.aggregate_outflow))
          : 0;

        // Populate containedAccounts from backend's contained_accounts list
        // Backend sends list of account UUIDs, we match them to AccountNode objects
        const containedAccountIds: string[] = Array.isArray(metadata.contained_accounts)
          ? metadata.contained_accounts
          : [];
        const containedAccounts: AccountNode[] = containedAccountIds
          .map((accId: string) => accountNodeById.get(accId))
          .filter((acc): acc is AccountNode => acc !== undefined);

        return {
          id: n.id,  // Keep original ID (e.g., "tier-B-lumped")
          type: 'lumped' as const,
          name: n.label,
          totalBalance: n.total,
          aggregateInflow,
          aggregateOutflow,
          tier,
          containedAccounts,  // Now populated from backend data
          accountCount,  // Use metadata count for display (fallback)
        };
      });

    // Extract and transform expense nodes
    const outflowNodes: OutflowNode[] = nodes
      .filter((n) => n.type === 'expense')
      .map((n) => ({
        id: mapNodeId(n.id, 'expense'),
        type: 'expense' as const,
        name: n.label,
        value: Math.abs(n.total),
        color: COLORS.expense,
        categoryId: n.entityId ?? n.id,  // Use entityId for navigation, fallback to id
      }));

    // Transform edges with proper ID mapping
    const allEdges: VisualizationFlowEdge[] = edges.map((e, index) => {
      // Find source and target node types for edge type determination
      const sourceNode = nodeMap.get(e.source);
      const targetNode = nodeMap.get(e.target);

      return {
        id: `edge-${index}-${e.source}-${e.target}`,
        source: mapNodeId(e.source, sourceNode?.type ?? 'unknown'),
        target: mapNodeId(e.target, targetNode?.type ?? 'unknown'),
        value: Math.abs(e.value),
        type: determineEdgeType(e.source, e.target, nodeMap),
      };
    });

    // WP-TRANSFER-2: Transfers are now EXCLUDED from edges
    // All edges in the main diagram are income or expense flows
    // Filter out any legacy transfer edges just in case
    const incomeExpenseEdges = allEdges.filter((e) => e.type !== 'transfer');

    // WP-TRANSFER-2: Use transferSection from backend API directly (deprecated)
    // This provides properly structured transfer data including single-sided transfers
    let transfers: TransferData[] = [];
    if (snapshot.transferSection?.transfers) {
      transfers = snapshot.transferSection.transfers.map((entry) => ({
        id: entry.id,
        sourceAccountId: entry.sourceAccountId,
        sourceAccountName: entry.sourceAccountName,
        targetAccountId: entry.targetAccountId,
        targetAccountName: entry.targetAccountName,
        amount: entry.amount,
        subcategory: entry.subcategory,
        transferType: entry.transferType,
        transactionIds: entry.transactionIds,
      }));
    }

    // WP-FLOW-REFACTOR-2: Use transferSankey from backend API directly
    // Pass through without transformation - TransferSankey component consumes API data directly
    let transferSankey: TransferSankeyData | undefined;
    if (snapshot.transferSankey) {
      transferSankey = {
        sourceNodes: snapshot.transferSankey.sourceNodes,
        targetNodes: snapshot.transferSankey.targetNodes,
        edges: snapshot.transferSankey.edges,
        totalAmount: snapshot.transferSankey.totalAmount,
        totalCount: snapshot.transferSankey.totalCount,
      };
    }

    return {
      inflowNodes,
      accountNodes,
      lumpedNodes,
      outflowNodes,
      edges: incomeExpenseEdges,
      transfers,
      transferSankey,
      summary: {
        totalIncome: summary?.totalIncome ?? 0,
        totalExpenses: summary?.totalExpenses ?? 0,
        netFlow: summary?.netFlow ?? 0,
        savingsRate: summary?.savingsRate ?? 0,
      },
    };
  }, [snapshot]);
}

export default useFlowData;
